import {Injectable} from '@angular/core';
import moment from 'moment-timezone';
import {Observable, of, throwError} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {marker} from '@biesbjerg/ngx-translate-extract-marker';
import {IJob} from '../batches/jobs';
import {IError} from './errors';
import {inRange, isNil} from 'lodash-es';
import {isValidDate} from "./validators/dates.validator";
import {map, take} from 'rxjs/operators';
import {IdName} from "./common-interfaces";


// translation support
const blackoutDaysTranslateMarkers = [
  marker('Sunday'),
  marker('Monday'),
  marker('Tuesday'),
  marker('Wednesday'),
  marker('Thursday'),
  marker('Friday'),
  marker('Saturday'),
];
export const blackoutDaysList = [
  {id: 1, description: 'Sunday'},
  {id: 2, description: 'Monday'},
  {id: 3, description: 'Tuesday'},
  {id: 4, description: 'Wednesday'},
  {id: 5, description: 'Thursday'},
  {id: 6, description: 'Friday'},
  {id: 7, description: 'Saturday'}];
/**
 * Stores any kind of independent functions that might be shared across various modules/components
 */
@Injectable({
  providedIn: 'root',
})
export class HelpersService {

  constructor(
    private translateService: TranslateService,
  /*  private switchLanguageService: SwitchLanguageService*/
  ) {
  }
  // https://en.wikipedia.org/wiki/List_of_tz_database_time_zones
  defaultTz = 'America/New_York'; // America/New_York	= Eastern (most areas)

  monthsArray = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];


  public static wrapError(response: any): IError {
    const statusCodesArray = [400, 401, 403, 404, 406, 422];
    const statusCode = response.status;

    let responseError = null;
    if ( statusCodesArray.includes(statusCode) ) {

      if (response.hasOwnProperty('error') && response['error'] !== null) {
        const errorObj = response['error'];
        if (typeof errorObj === 'string') {
          responseError = errorObj;
        } else if (errorObj.hasOwnProperty('errorMessage') && errorObj['errorMessage'] !== null) {
          responseError = errorObj['errorMessage'];
        }
      } else if (response.hasOwnProperty('message') && response['message'] != null) {
        responseError = response['message'];
      } else if (response.hasOwnProperty('errorMessage') && response['errorMessage'] != null) {
        responseError = response['errorMessage'];
      }
    }

    if (statusCode === 400) {
      return {
        status: 400,
        errorMessage: responseError ? responseError : '400 - Bad Request',
      };
    }

    if (statusCode === 401) {
      return {
        status: 401,
        errorMessage: responseError ? responseError : '401 - Unauthorized',
      };
    }

    if (statusCode === 403) {
      return {
        status: 403,
        errorMessage: responseError ? responseError : '403 - Forbidden',
      };
    }

    if (statusCode === 404) {
      return {
        status: 404,
        errorMessage: responseError ? responseError : '404 - Not Found',
      };
    }

    if (statusCode === 406) {
      return {
        status: 406,
        errorMessage: '406 - Not Acceptable', // response error not helpful here
      };
    }

    if (statusCode === 422) {
      let errorDetails;
      if(responseError) {
        try {
          errorDetails = JSON.parse(responseError)['errorMessage'];
        } catch (e) {
          errorDetails = responseError;
        }
      } else {
        errorDetails = '422 - Unprocessable Entity';
      }
      return {
        status: 422,
        errorMessage: errorDetails,
      };
    }


    if (statusCode === 500) {
      return {
        status: 500,
        errorMessage: '500 - Internal Server Error',
      };
    }

    // handle unknown errors
    if (response.status === 0) {
      return {
        status: 0,
        errorMessage: 'Unknown Error - please check your internet connection.',
      };
    }

    throw new Error('oops');
  }

  getCancelMessage() {
    let message = '';
    this.translateService.stream(marker('Any unsaved changes will be lost.'))
      .subscribe(
        (response) => message = response,
      );
    return message;
  }

  extractTimeFromTimestamp(ts) {
    if (ts === 0 || ts === null) {
      return null;
    }
    // from 1572696000000 to 14:11 AM
    return moment(ts).format('HH:mm:ss');
  }

  extractTimeInStoreTxForEdit(item: IJob, field: string) {
    if (item[field] === null) {
      return null;
    }

    const xtz = item.storeTimezone !== null ? item.storeTimezone : this.defaultTz;
    return moment(item[field]).tz(xtz).format('HH:mm:ss');
  }

  extractTimeForEdit(dateField: Date, timezone: string) {
    if (isNil(dateField)) {
      return null;
    }

    if (isNil(timezone)) {
      timezone = this.defaultTz;
    }

    return  moment(dateField).tz(timezone).format('HH:mm');
  }


  formatDate(d) {
    if (d === null) {
      return '';
    }

    return moment(d).format(this.getCurrentLanguageFormat());
  }

  formatToShortDate(d) {
    if (localStorage.getItem('language') === 'es') {
      return moment(d).format('DD/MM/YYYY');
    }
    else {
      return moment(d).format('MM/DD/YYYY');
    }
  }

  formatToShortDateTime(d) {
    if (localStorage.getItem('language') === 'es') {
      return moment(d).format('DD/MM/YYYY H:mm');
    }
    else {
      return moment(d).format('MM/DD/YYYY hh:mm A');
    }
  }

  formatDateDatePicker(d) {
    if (d === null) {
      return '';
    }

    return moment(d).format('M/DD/YYYY');
  }

  formatDateTimeDatePicker(d) {
    if (d === null) {
      return '';
    }

    return moment(d).format('M/DD/YYYY, hh:mm:ss');
  }

  numberOfDaysBetweenDates(startDate: number[], endDate: number[]): number|null {
    if ( !(startDate && endDate)) {
      return null;
    }
    const start = moment(new Date(Date.UTC(startDate[0], startDate[1] - 1, startDate[2])));
    const end = moment(new Date(Date.UTC(endDate[0], endDate[1] - 1, endDate[2])));
    return moment.duration(end.diff(start)).asDays();
  }

  arrayToDateInBrowserTz(dateArray: number[]): Date {
    if (dateArray === null) {
      return null;
    }
    return this.toDateInBrowserTz(dateArray[0], dateArray[1] - 1, dateArray[2]);
  }

  formatNumber(
    number: number,
    options: {
      minimumFractionDigits?: number,
      maximumFractionDigits?: number
    } = {minimumFractionDigits: 0}
  ): string {
    const language = localStorage.getItem('language');

    if (language === 'en') {
      return number.toLocaleString('en-US', options);
    } else {
      return number.toFixed(options.minimumFractionDigits);
    }
  }

  private toDateInBrowserTz(year, month, day): Date {
    var newDate = moment();
    newDate.set('year', year);
    newDate.set('month', month);
    newDate.set('date', day);
    newDate.startOf('day');

    return newDate.toDate();
  }


  formatDateFromParts(d:number[]) {
    const date = this.arrayToDateInBrowserTz(d);
    if (date === null) {
      return '';
    }
    return moment(date).format(this.getCurrentLanguageFormat());
  }

  formatDateToISO(d) {
    if (d === null) {
      return '';
    }
    return moment(d).format('YYYY-MM-DD');
  }

  formatDateToTraditionalFormat(d) {
    if (d === null) {
      return '';
    }

    if(localStorage.getItem('language') === 'es') {
      return moment(d).format('DD-MM-YYYY');
    }
    else {
      return moment(d).format('MM-DD-YYYY');
    }
  }

  formatDateToTimestamp(d) {
    if (d === null) {
      return '';
    }
    return moment(d).format('MMM DD YYYY');
  }


  formatDateAndTimeInTz(date: number | Date, timezone?: string) {
    return this.formatDateInTz(date, timezone) + ' ' + this.formatTimeInTz(date, timezone);
  }

  formatDateInTz(date: number | Date | string, timezone?: string) {
    return this.formatInTz(date, this.getCurrentLanguageFormat(), timezone);
  }

  formatExportDateTimeInTz(date: number | Date, timezone?: string) {
    return this.formatInTz(date, "YYYY-MM-DD HH:mm", timezone);
  }

  formatExportTimeInTz(date: number | Date | string, timezone?: string) {
    if (typeof date === 'string') {
      //for schedules times are received as formatted strings
      return date;
    }

    return this.formatInTz(date, "HH:mm", timezone);
  }

  formatExportDateInTz(date: number | Date, timezone?: string) {
    return this.formatInTz(date, "YYYY-MM-DD", timezone);
  }


  formatTimeInTz(unixDate: number | Date | string, timezone?: string) {
    if (typeof unixDate === 'string') {
      //for schedules times are received as formatted strings
      return unixDate;
    }
    return this.formatInTz(unixDate, 'h:mm A z', timezone);
  }

  private formatInTz(date: number | Date | string, pattern: string, timezone?: string) {
    if (date === null || date === undefined || date === '') {
      return '';
    }

    let unixDate = null;
    if (date instanceof Date) {
      unixDate = date.getTime();
    } else {
      unixDate = date;
    }

    const validTz = timezone !== null && timezone !== undefined;
    return moment(unixDate).tz(validTz ? timezone : this.defaultTz).format(pattern);
  }


  localDateToUTCTimestamp(d) {
    if (d === null) {
      return null;
    }
    const localTZString = moment(d).format('YYYY-MM-DD') + ' 00:00:00';
    const sdt = moment.tz(localTZString, 'YYYY-MM-DD HH:mm:ss', 'UTC');
    return moment(sdt).unix() * 1000;
  }

  localDateTimeToTimestamp(date: Date, tz: string) : number{
    if (!date || !isValidDate(date)) {
      return null;
    }

    const localTZString = moment(date).format('YYYY-MM-DD HH:mm:ss');
    const sdt = moment.tz(localTZString, 'YYYY-MM-DD HH:mm:ss', tz);
    return moment(sdt).unix() * 1000;
  }

  toDate(timestamp: number, tz: string) : Date {
    if (timestamp) {
      const xtz = tz ? tz : this.defaultTz;
      const ret = moment(timestamp).tz(xtz);
      // YYYY-MM-DD hh:mm A is failing in Safari
      return new Date(ret.format('YYYY/MM/DD hh:mm A'));
    }
    return null;
  }

  flattenArray(arr: Array<any>): Array<any> {
    let newArray = arr.reduce((accumulator, item) => {
      if(Array.isArray(item)) {
        accumulator = accumulator.concat(this.flattenArray(item));
      } else {
        accumulator.push(item);
      }
      return accumulator;
    }, []);
    return newArray;
  }

  enumerateDaysBetweenDates(startDate:string, endDate:string) {
    var dates = [];
    const browserTimezone = moment.tz.guess();
    let start = moment(startDate).tz(browserTimezone);
    const end = moment(endDate).tz(browserTimezone);
    while(start <= end){
      dates.push(start.toISOString().substring(0, 10));
      start = moment(start).add(1, 'days');
    }

    return dates;
  };

  showManagerUserName(item) {
    return item.managerLastName + ', ' + item.managerFirstName + ' (' + item.managerUserName + ')';
  }

  firstLetterUpper(str) {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  formatWeekdays(days) {
    const response = [];
    days.forEach(el => {
      response.push(this.firstLetterUpper(el));
    });
    return response.join(', ');
  }

  showBooleanAsYesNo(x) {
    if (x === null) {
      return '';
    }
    if (x) {
      return 'Yes';
    } else {
      return 'No';
    }
  }

  generateTimestampFromDateAndTime(d, t) {

    if (d === null || t === null) {
      return null;
    }
    if (t.length === 5) {
      t = t + ':00';
    }

    const dd = moment(d, 'YYYY MMM DD').format('YYYY-MM-DD') + ' ' + t;
    const sdt = moment(dd, 'YYYY-MM-DD hh:mm A');

    // format 'x' means timestamp in milliseconds
    return sdt.format('x');
  }

  isStartTimeBeforeEndTime(params) {
    const anyDay = '2010 May 10';
    let message = '';
    const startDateTime = this.generateTimestampFromDateAndTime(anyDay, params.startTime);
    const endDateTime = this.generateTimestampFromDateAndTime(anyDay, params.endTime);
    if (startDateTime > endDateTime) {
      this.translateService.stream(marker('Start Time is after End Time.'))
      .subscribe((response) => message = response);
      return {
        status: false,
        message: message,
      };
    } else {
    return {
      status: true,
    };
    }
  }

  generateUnixDateFromDateAndTimeAndTimezone(d, t, tz) : number {

    if (d === null || d === '' || t === null || t === '') {
      return null;
    }

    const xtz = tz !== null ? tz : this.defaultTz;
    const dd = moment(d).format('YYYY-MM-DD') + ' ' + t;
    const sdt = moment.tz(dd, xtz);

    // format 'x' means timestamp in milliseconds
    const ret = sdt.format('x');

    if(!isNil(ret)) {
      return +ret;
    }

    return null;
  }

  toUTCDate(date: Date): number {
    // ignore local timezone - use UTC and set hour at mid day
    return  Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0);
  }

  toUTCDateTime(date: Date): number {
    // ignore local timezone - use UTC
    return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());
  }


  utcTimestampToLocalDate(timestamp: number): Date {
    const utcString = moment.tz(timestamp, 'UTC').format('YYYY-MM-DD');
    return moment(utcString).toDate();
  }


  generateUnixDateFromDate(d) {
    const sdt = moment(d);
    return moment(sdt).unix() * 1000;
  }

  sliceText(text: string, max: number) {

    if (text === null || text === undefined) {
      return '';
    }
    if (text.length <= max || typeof(text) !== 'string') {
      return text;
    }
    return text.slice(0, max) + '...';
  }

  showEmptyString(x) {
    if (x === null) {
      return '';
    }
  }

  public handleError(response: any): Observable<any> {
    try {
      const wrappedError =  HelpersService.wrapError(response);
      return of(wrappedError);
    } catch (e) {
      return throwError(e);
    }
  }

  getWeeklyComplianceStartDay(type: string) {
    const days = {
      'Sunday': 0,
      'Monday': 1,
      'Tuesday': 2,
      'Wednesday': 3,
      'Thursday': 4,
      'Friday': 5,
      'Saturday': 6,
    };
    const user =  JSON.parse(localStorage.getItem('currentUser'));
    const orgId = +localStorage.getItem('selectedOrganization');
    const d = user.organizations.filter(item => item.id === orgId)[0].weeklyComplianceStartDay;
    if (type === 'string') {
      return d;
    } else {
      return days[d];
    }
  }

  showFormType(type: boolean) {
    let visits;
    this.translateService.stream(marker('Visit Form'))
      .subscribe(response => visits = response);
    let adhoc;
    this.translateService.stream(marker('Ad Hoc Form'))
      .subscribe(response => adhoc = response);
    return type ? visits : adhoc;
  }

  formatNameAsFirstLastNick(name) {
    return name.firstName + ', ' + name.lastName + '(' + name.username + ')';
  }

  formatVisitorNameAsLastFirstUsername(name) {
    return name.visitorLastName + ', ' + name.visitorFirstName + ' (' + name.visitorUserName + ')';
  }

  formatVisitorFullName(name) {
    return name.visitorFirstName + ', ' + name.visitorLastName;
  }

  getMonth(monthIndex: number): Observable<string> {
    return this.translateService.stream(this.monthsArray[monthIndex]);
  }

  getMonthYear(date: Date): Observable<IdName> {
    return this.translateService.stream(this.monthsArray[date.getMonth()]).pipe(
      map(translatedMonth => {
        return {
          id: date.getMonth(),
          name: translatedMonth + ' ' + date.getFullYear()
        }
      }),
      take(1)
    );
  }


  arraysEqual(a: any[], b: any[]): boolean {
    if (a === b) {
      return true;
    }
    if (a == null || b == null) {
      return false;
    }
    if (a.length !== b.length) {
      return false;
    }

    // If you don't care about the order of the elements inside
    // the array, you should sort both arrays here.
    // Please note that calling sort on an array will modify that array.
    // you might want to clone your array first.
    for (let i = 0; i < a.length; ++i) {
      if (a[i] !== b[i]) {
        return false;
      }
    }
    return true;
  }

  isValidDate(dateString: string, format = 'MM/DD/YYYY'): boolean {
    if(dateString === null || dateString.length === 0) {
      //empty values
      return true;
    }

    return this.dateInValidRange(dateString, format) &&
      moment(dateString, format).isValid();
  }

  formatDuration(duration: number): string {
    const hours = Math.floor(duration / 60 / 60);
    const minutes = Math.floor((duration - hours * 60 * 60) / 60);
    const seconds = duration - hours * 60 * 60 - minutes * 60;

    const hoursStr = hours.toString();
    const minutesStr = minutes < 10 ? '0' + minutes.toString() : minutes.toString();
    const secondsStr = seconds < 10 ? '0' + seconds.toString() : seconds.toString();

    return hoursStr + ':' + minutesStr + ':' + secondsStr;
  }

  formatMinutesAsFullHours(duration: number): string {
    if (!duration || duration === 0) {
      return '';
    }
    return (duration/60).toString() + ' h';
  }

  minutesToHoursDuration(duration) {
    const hours = (duration / 60);
    const rhours = Math.floor(hours);
    const minutes = (hours - rhours) * 60;
    const rminutes = Math.round(minutes);
    let hoursTxt;
    let minutesTxt;

    this.translateService.stream(marker(`hours`)).pipe(
      take(1),
    ).subscribe((response) => {
      hoursTxt = response;
    });

    this.translateService.stream(marker(`minutes`)).pipe(
      take(1),
    ).subscribe((response) => {
      minutesTxt = response;
    });

    if(rhours && rminutes) {
      return `${rhours} ${hoursTxt} ${rminutes} ${minutesTxt}`
    }
    if (rhours && !rminutes) {
      return `${rhours} ${hoursTxt}`
    }
    if (!rhours && rminutes) {
      return `${rminutes} ${minutesTxt}`
    }
  }

  /**
   * Get workdays based on blackout days
   * If consecutive days then return firstDay - LastDay else day1|day2|day3...
   * @param blackoutDaysIdsString '1|2|3'
   */
  getWorkDaysString(blackoutDaysIdsString: string) {
    const blackoutDaysIds = blackoutDaysIdsString.split('|').map(str => +str);

    const workDays = blackoutDaysList.filter(item => !blackoutDaysIds.includes(item.id));

    if(workDays.length > 1) {
      if(this.areConsecutiveNumbers(workDays.map(day => day.id))) {
        return `${workDays[0].description} - ${workDays[workDays.length-1].description}`
      } else {
        return workDays.map(item => item.description).join(' | ');
      }
    } else {
      return `${workDays[0].description}`
    }
  }

  private areConsecutiveNumbers(ids: number[]) {
    if(ids.length > 1) {
      for (let i = 0; i < ids.length; i++) {
        if (i + 1 < ids.length) {
          if (ids[i] != ids[i + 1] - 1) {
            return false;
          }
        }
      }
    }
    return true;
  }

  downloadBlob(blob: Blob, filename: string) {
    const blobURL = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = encodeURI(blobURL);
    a.download = encodeURI(filename);
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(blobURL);
    document.body.removeChild(a);
  }

  createDateFromPartsInTz(parts: Array<number>, tz: string) {

    if (parts.length === 0) {
      return '';
    }
    const [year, month, day] = parts;
    const d = moment.tz(Date.UTC(year, (month - 1), day).toString(), 'x', tz);
    return d.format('YYYY-MM-DD');
  }

  createDateInUTC(date: string) {
    let [day, time] = date.split('T');
    if (time === undefined) {
      time = '08:00:00.000';
    }
    const newdate = moment.utc(`${[day, time].join()}`);

    return newdate.format('YYYY-MM-DDTHH:mm:ss');
  }

  createDateFromTimestampInTz(timestamp: number, tz: string) {
    if (timestamp === null) {
      return null;
    }
    const d = moment(timestamp).tz(tz);
    return d.format('YYYY-MM-DDTHH:mm:ss.SSS');
  }

  extractTimeTimestampInTz(timestamp: number, tz: string) {
    const d = moment.tz(`${timestamp}`, 'x', tz);
    return d.format('HH:mm a');
  }

  isNullOrEmpty(field: null | string): boolean {
    return field === null || field === '';
  }

  validYYYYMMDDOrNull(d: string): Date | null {
    const date = moment(d, 'YYYY-MM-DD');
    return date.isValid() ? date.toDate() : null;
  }

  isToday(timestamp: string | number, tz: string): boolean {

    const xtz = tz !== null ? tz : 'America/New_York';
    const d = moment(timestamp).tz(xtz).format('YYYY-MM-DD');
    const today = moment().tz(xtz).format('YYYY-MM-DD');

    return today === d;
  }

  private dateInValidRange(dateString: string, dateFormat: string): boolean {
    const regExp = new RegExp('^[0-9_/]*$')
    if(!regExp.test(dateString)) {
      //invalid chars
      return false;
    }

    let month, year, day;
    switch (dateFormat) {
      case 'MM/DD/YYYY':
        [month, day, year] = dateString.split('/');
        break;
      case 'YYYY-MM-DD' :
        [year, month, day] = dateString.split('-');
        break;
      default:
        break;
    }

    return inRange(parseInt(year), 1970, 2100) &&
      inRange(parseInt(day), 1, 32) &&
      inRange(parseInt(month), 1, 13);

  }

  timeFromAMPMTo24H(time: string): string {
    const [thetime, period] = time.split(' ');
    if(period === 'PM') {
      const [hours, mins] = thetime.split(':');
      let h = `${(Number(hours) + 12)%24}`;
      if (h === '0') {
        h = '00';
      }
      return `${h}:${mins}`;
    }
    return thetime;
  }

  setIdsToMatTabs() {
    let id = 1;
    const classArr: any = document.querySelectorAll('.mat-ripple.mat-tab-label.mat-focus-indicator');
    classArr.forEach(element => {
        element.setAttribute('id', `tab${id}`);
        id++;
    });
  }

  public trimListToTop(comaSepList: string, top: number) {
    if (isNil(comaSepList)) {
      return '';
    }

    const elements = comaSepList.split(',');
    if (elements.length > top) {
      const items = elements.filter((el,i) => i<top);
      return items.join(', ') + ' …';
    }
    return elements.join(', ');
  }

  private readonly ES_DATE_FORMAT = 'DD MMM YYYY';
  private readonly US_DATE_FORMAT = 'MMM DD YYYY';

  private getCurrentLanguageFormat(): string {
    // localStorage.getItem('language') ->  this.switchLanguageService.getSavedLanguageCode()

    if (localStorage.getItem('language') === 'es') {
      return this.ES_DATE_FORMAT;
    }

    //default always US format
    return this.US_DATE_FORMAT;
  }

  browserTimezone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
}
